/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Every time zone the runtime knows.                                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **From the RUNTIME, not from a hardcoded array.** The mockup ships sixteen zones; somebody in
 * Reykjavík is not a bug report. `Intl.supportedValuesOf('timeZone')` is the browser's own IANA
 * database, which is the same authority the DATABASE checks against (`is_valid_timezone` asks
 * Postgres to use the name) — so a zone the picker offers is a zone the server accepts, without a
 * third list to keep in step.
 *
 * ⚠️⚠️ **EXTRACTED FROM `preferences-section.tsx` FOR ITEM 49**, where the person's own time zone
 * became a searchable dropdown. It was a free-text field there — so a typo saved a zone the server
 * would refuse, and the refusal arrived as a validation error about a name nobody could see was
 * wrong. Two copies of this function is how the account preferences and the person rail come to
 * offer different zones.
 */

/**
 * ⚠️ **`supportedValuesOf` is not in every engine**, and a missing list must not empty the picker —
 * that would silently make the field unchangeable. Falling back to the stored value keeps the
 * control honest: it shows what is set and offers nothing else.
 */
export function zonesFor(current: string): readonly string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;

  if (typeof supported !== 'function') return [current];
  try {
    const zones = supported('timeZone');
    return zones.length > 0 ? zones : [current];
  } catch {
    return [current];
  }
}

/**
 * The zones as picker options, with the stored one guaranteed present.
 *
 * ⚠️⚠️ **A ZONE THE RUNTIME DOES NOT KNOW IS STILL OFFERED, AND DROPPING IT WOULD BE A SILENT
 * EDIT.** A person's stored zone may be one this browser's ICU has never heard of — an old
 * alias, a zone added since the browser shipped. A `Select` whose `value` is not among its options
 * renders as empty, and the next save of any other field would carry that emptiness along.
 */
export function zoneOptions(current: string): { value: string; label: string }[] {
  const zones = zonesFor(current);
  const options = zones.map((zone) => ({ value: zone, label: zone }));
  if (current !== '' && !zones.includes(current)) {
    options.unshift({ value: current, label: current });
  }
  return options;
}
